import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import dataService from '../services/dataService';

import {
  Container,
  Paper,
  Box,
  Typography,
  Button,
  Stack,
  Avatar,
  Card,
  CardContent,
  Grid,
  Chip,
  TextField,
  InputAdornment,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
} from '@mui/material';
import RoomServiceIcon from '@mui/icons-material/RoomService';
import SearchIcon from '@mui/icons-material/Search';
import WarningIcon from '@mui/icons-material/Warning';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WhatshotIcon from '@mui/icons-material/Whatshot';

// Common allergens for quick filtering
const COMMON_ALLERGENS = [
  { label: 'Gluten', color: 'warning' },
  { label: 'Dairy', color: 'info' },
  { label: 'Nuts', color: 'error' },
  { label: 'Shellfish', color: 'secondary' },
  { label: 'Eggs', color: 'warning' },
  { label: 'Soy', color: 'success' },
  { label: 'Vegan', color: 'success' },
  { label: 'Vegetarian', color: 'primary' },
];

// Ingredient keywords that indicate allergen presence (for scanning descriptions)
const ALLERGEN_KEYWORDS = {
  Gluten: ['bread', 'flour', 'wheat', 'pasta', 'noodle', 'tortilla', 'bun', 'crouton', 'breaded', 'panko', 'roux', 'soy sauce', 'teriyaki', 'tempura', 'croissant', 'bagel', 'roll', 'pita', 'brioche', 'crust', 'toast', 'sandwich'],
  Dairy: ['cheese', 'cream', 'milk', 'butter', 'yogurt', 'parmesan', 'mozzarella', 'cheddar', 'brie', 'gouda', 'feta', 'ricotta', 'mascarpone', 'whey', 'casein', 'ghee', 'burrata', 'provolone', 'gruyere', 'swiss', 'blue cheese', 'gorgonzola', 'ranch', 'alfredo', 'queso', 'crema', 'sour cream', 'ice cream', 'gelato', 'parm'],
  Nuts: ['nut', 'almond', 'walnut', 'pecan', 'cashew', 'pistachio', 'hazelnut', 'peanut', 'macadamia', 'pine nut', 'praline', 'marzipan', 'nougat', 'pesto'],
  Eggs: ['egg', 'mayo', 'mayonnaise', 'aioli', 'hollandaise', 'custard', 'meringue', 'mousse', 'frittata', 'quiche', 'carbonara', 'caesar dressing', 'remoulade', 'tartar sauce'],
  Shellfish: ['shrimp', 'crab', 'lobster', 'crawfish', 'crayfish', 'prawn', 'scallop', 'clam', 'mussel', 'oyster', 'calamari', 'squid', 'octopus'],
  Fish: ['fish', 'salmon', 'tuna', 'cod', 'halibut', 'tilapia', 'trout', 'bass', 'snapper', 'mahi', 'swordfish', 'anchovy', 'sardine', 'mackerel', 'caviar', 'roe'],
  Soy: ['soy', 'tofu', 'edamame', 'miso', 'tempeh', 'soy sauce', 'teriyaki'],
  Sesame: ['sesame', 'tahini', 'hummus', 'halvah'],
};

// Check if text contains any allergen keywords
const textContainsAllergen = (text, allergen) => {
  if (!text || !ALLERGEN_KEYWORDS[allergen]) return false;
  const lowerText = text.toLowerCase();
  return ALLERGEN_KEYWORDS[allergen].some(keyword => lowerText.includes(keyword));
};

// Get all allergens detected in item (both tagged and inferred from text)
const getItemAllergens = (item) => {
  const tagged = item.allergens || [];
  const inferred = [];

  const textToScan = `${item.name || ''} ${item.description || ''}`.toLowerCase();

  Object.keys(ALLERGEN_KEYWORDS).forEach(allergen => {
    // Skip if already tagged
    if (tagged.some(t => t.toLowerCase() === allergen.toLowerCase())) return;

    if (ALLERGEN_KEYWORDS[allergen].some(keyword => textToScan.includes(keyword))) {
      inferred.push(allergen);
    }
  });

  return { tagged, inferred };
};

const ServerDashboardScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const restaurantId = location.state?.restaurantId;
  const restaurantName = location.state?.restaurantName;

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAllergens, setSelectedAllergens] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [specials, setSpecials] = useState([]);
  const [upsellTips, setUpsellTips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchResults, setSearchResults] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      if (!restaurantId && restaurantId !== 0) {
        setError('No restaurant selected');
        setLoading(false);
        return;
      }

      try {
        const data = await dataService.getRestaurant(restaurantId);

        if (!data || data.error) {
          setError(data?.error || 'Failed to load restaurant data');
          setLoading(false);
          return;
        }

        // Extract menu items from all menus
        const allMenuItems = [];
        const allSpecials = [];

        if (data.menus && data.menus.length > 0) {
          data.menus.forEach((menu) => {
            let menuData = menu.menu_data;

            // Parse if it's a string
            if (typeof menuData === 'string') {
              try {
                menuData = JSON.parse(menuData);
              } catch {
                // Legacy text format - try basic parsing
                menuData = parseLegacyMenu(menuData);
              }
            }

            // Extract items from structured JSON (v2 format: menus → categories → items)
            if (menuData?.menus && menuData.menus.length > 0) {
              menuData.menus.forEach((menu) => {
                if (menu.categories) {
                  menu.categories.forEach((category) => {
                    if (category.items) {
                      category.items.forEach((item) => {
                        if (item.available !== false) {
                          allMenuItems.push({
                            id: item.id,
                            name: item.name,
                            description: item.description || '',
                            price: item.price,
                            price_type: item.price_type || 'FIXED',
                            prep_methods: item.prep_methods || [],
                            allergens: (item.allergens || []).map(a =>
                              a.charAt(0).toUpperCase() + a.slice(1).toLowerCase()
                            ),
                            tags: (item.dietary_tags || []).map(t =>
                              t.charAt(0).toUpperCase() + t.slice(1).toLowerCase()
                            ),
                            category: category.name,
                            menu: menu.name,
                          });
                        }
                      });
                    }
                  });
                }
              });
            }

            // Extract specials
            if (menuData?.specials) {
              menuData.specials.forEach((special) => {
                allSpecials.push({
                  id: special.id,
                  name: special.name,
                  description: special.description || '',
                  price: formatPrice(special.price, menuData.currency, special.price_type),
                  available_days: special.available_days || [],
                });
              });
            }

            // Extract upsell tips
            if (menuData?.upsell_tips) {
              const enabledTips = menuData.upsell_tips
                .filter(tip => tip.enabled !== false)
                .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
              setUpsellTips(enabledTips);
            }
          });
        }

        setMenuItems(allMenuItems);
        setSpecials(allSpecials);
        setLoading(false);
      } catch (err) {
        console.error('Error loading restaurant data:', err);
        setError('Failed to load menu data');
        setLoading(false);
      }
    };

    loadData();
  }, [restaurantId]);

  // Parse legacy text menu format
  const parseLegacyMenu = (text) => {
    const lines = text.split('\n').filter(l => l.trim());
    const items = [];

    lines.forEach(line => {
      // Try to extract dish name and price
      const priceMatch = line.match(/\$?\d+\.?\d*/);
      if (priceMatch) {
        const name = line.replace(priceMatch[0], '').replace(/[:-]/g, '').trim();
        if (name) {
          items.push({
            id: crypto.randomUUID(),
            name,
            price: parseFloat(priceMatch[0].replace('$', '')),
            allergens: [],
            dietary_tags: [],
          });
        }
      }
    });

    return {
      version: 2,
      menus: items.length > 0 ? [
        {
          id: crypto.randomUUID(),
          name: 'Dinner',
          meal_period: 'dinner',
          display_order: 1,
          categories: [{ id: crypto.randomUUID(), name: 'Menu Items', display_order: 1, items }]
        }
      ] : [],
      specials: [],
      upsell_tips: [],
    };
  };

  const formatPrice = (price, currency = 'USD', priceType = 'FIXED') => {
    // Handle Market Price items
    if (priceType === 'MP' || price === null) {
      return 'MP';
    }
    if (typeof price !== 'number') return price;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(price);
  };

  const toggleAllergen = (allergen) => {
    setSelectedAllergens((prev) =>
      prev.includes(allergen)
        ? prev.filter((a) => a !== allergen)
        : [...prev, allergen]
    );
  };

  const handleSearch = useCallback(() => {
    if (!searchQuery.trim() && selectedAllergens.length === 0) {
      setSearchResults(null);
      return;
    }

    const results = menuItems.filter((item) => {
      const matchesSearch = searchQuery
        ? item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (item.description && item.description.toLowerCase().includes(searchQuery.toLowerCase()))
        : true;

      const matchesAllergens = selectedAllergens.length > 0
        ? selectedAllergens.every((allergen) => {
            // Check explicitly tagged allergens
            const hasTaggedAllergen = item.allergens?.some(a =>
              a.toLowerCase() === allergen.toLowerCase()
            );
            const hasTag = item.tags?.some(t =>
              t.toLowerCase() === allergen.toLowerCase()
            );

            // Also scan name and description for allergen keywords
            const nameHasAllergen = textContainsAllergen(item.name, allergen);
            const descHasAllergen = textContainsAllergen(item.description, allergen);
            const hasAllergenInText = nameHasAllergen || descHasAllergen;

            // For dietary tags (Vegan, Vegetarian), we want items that HAVE the tag
            if (['Vegan', 'Vegetarian'].includes(allergen)) {
              return hasTag;
            }
            // For allergens, we want items that DON'T have the allergen
            // Check both the allergens array AND the description text
            return !hasTaggedAllergen && !hasAllergenInText;
          })
        : true;

      return matchesSearch && matchesAllergens;
    });

    setSearchResults(results);
  }, [searchQuery, selectedAllergens, menuItems]);

  useEffect(() => {
    handleSearch();
  }, [handleSearch]);

  const clearFilters = () => {
    setSelectedAllergens([]);
    setSearchQuery('');
    setSearchResults(null);
  };

  // Check if today matches special's available days
  const isSpecialAvailableToday = (special) => {
    if (!special.available_days || special.available_days.length === 0) {
      return true; // Always available if no days specified
    }
    const today = new Date().toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
    return special.available_days.some(day => day.toLowerCase() === today);
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }} color="text.secondary">
          Loading menu data...
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/dashboard')}>
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  const todaysSpecials = specials.filter(isSpecialAvailableToday);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/dashboard')}
            variant="text"
          >
            Back
          </Button>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Avatar
            sx={{
              width: 64,
              height: 64,
              bgcolor: 'info.main',
              mx: 'auto',
              mb: 2,
            }}
          >
            <RoomServiceIcon sx={{ fontSize: 32 }} />
          </Avatar>
          <Typography variant="h5" fontWeight={700}>
            Server Assistant
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {restaurantName}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {menuItems.length} menu items loaded
          </Typography>
        </Box>
      </Paper>

      <Grid container spacing={3}>
        {/* Left Column - Allergen Check */}
        <Grid item xs={12} md={8}>
          <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <WarningIcon color="error" />
              <Typography variant="h6" fontWeight={600}>
                Quick Allergen Check
              </Typography>
            </Box>

            {/* Search Field */}
            <TextField
              fullWidth
              placeholder="Search menu items..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />

            {/* Allergen Filter Chips */}
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Filter by dietary needs:
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
              {COMMON_ALLERGENS.map((allergen) => (
                <Chip
                  key={allergen.label}
                  label={allergen.label}
                  color={selectedAllergens.includes(allergen.label) ? allergen.color : 'default'}
                  variant={selectedAllergens.includes(allergen.label) ? 'filled' : 'outlined'}
                  onClick={() => toggleAllergen(allergen.label)}
                  sx={{ mb: 1 }}
                />
              ))}
              {selectedAllergens.length > 0 && (
                <Chip
                  label="Clear All"
                  variant="outlined"
                  onDelete={clearFilters}
                  sx={{ mb: 1 }}
                />
              )}
            </Stack>

            {/* No Menu Data Warning */}
            {menuItems.length === 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                No menu items found. Please ensure the menu has been set up in the Menu Manager.
              </Alert>
            )}

            {/* Results */}
            {searchResults !== null && (
              <>
                <Divider sx={{ my: 2 }} />
                <Alert
                  severity={searchResults.length > 0 ? 'success' : 'warning'}
                  sx={{ mb: 2 }}
                >
                  {searchResults.length > 0
                    ? `Found ${searchResults.length} safe options`
                    : 'No items match the selected filters'}
                </Alert>
                <List>
                  {searchResults.map((item) => (
                    <ListItem
                      key={item.id}
                      sx={{
                        bgcolor: 'grey.50',
                        borderRadius: 2,
                        mb: 1,
                      }}
                    >
                      <ListItemIcon>
                        <CheckCircleIcon color="success" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography fontWeight={500}>{item.name}</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                              {formatPrice(item.price, 'USD', item.price_type)}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Box>
                            {item.description && (
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                {item.description}
                              </Typography>
                            )}
                            <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                              {/* ALLERGENS FIRST - Bold, filled variant for visibility */}
                              {(() => {
                                const { tagged, inferred } = getItemAllergens(item);
                                const hasAnyAllergen = tagged.length > 0 || inferred.length > 0;
                                return (
                                  <>
                                    {tagged.map((a) => (
                                      <Chip
                                        key={a}
                                        label={a}
                                        size="small"
                                        variant="filled"
                                        color="error"
                                        sx={{ mb: 0.5, fontWeight: 700 }}
                                      />
                                    ))}
                                    {inferred.map((a) => (
                                      <Chip
                                        key={`inferred-${a}`}
                                        label={`${a}*`}
                                        size="small"
                                        variant="outlined"
                                        color="warning"
                                        sx={{ mb: 0.5, fontStyle: 'italic' }}
                                        title="Detected from description"
                                      />
                                    ))}
                                    {!hasAnyAllergen && !item.tags?.length && (
                                      <Chip label="No common allergens" size="small" color="success" />
                                    )}
                                  </>
                                );
                              })()}

                              {/* Prep Methods - visible and clear */}
                              {item.prep_methods && item.prep_methods.length > 0 && item.prep_methods.map((method) => (
                                <Chip
                                  key={method}
                                  label={method}
                                  size="small"
                                  color="info"
                                  variant="outlined"
                                  sx={{ mb: 0.5 }}
                                />
                              ))}

                              {/* Dietary tags - secondary priority */}
                              {item.tags?.length > 0 && item.tags.map((t) => (
                                <Chip key={t} label={t} size="small" color="success" variant="outlined" sx={{ mb: 0.5 }} />
                              ))}
                            </Stack>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </Paper>
        </Grid>

        {/* Right Column - Specials & Upsell */}
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <WhatshotIcon color="secondary" />
              <Typography variant="h6" fontWeight={600}>
                Today's Specials
              </Typography>
            </Box>

            {todaysSpecials.length > 0 ? (
              <Stack spacing={2}>
                {todaysSpecials.map((special) => (
                  <Card key={special.id} variant="outlined">
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {special.name}
                      </Typography>
                      {special.description && (
                        <Typography variant="body2" color="text.secondary">
                          {special.description}
                        </Typography>
                      )}
                      {special.price && (
                        <Typography variant="subtitle2" color="secondary.main" sx={{ mt: 0.5 }}>
                          {special.price}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No specials for today. Check Menu Manager to add specials.
              </Typography>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Upsell Tips */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <LocalOfferIcon color="primary" />
              <Typography variant="h6" fontWeight={600}>
                Upsell Tips
              </Typography>
            </Box>
            {upsellTips.length > 0 ? (
              <List dense>
                {upsellTips.map((tip) => (
                  <ListItem key={tip.id}>
                    <ListItemText
                      primary={tip.title}
                      secondary={tip.body}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No upsell tips configured. Add tips in Menu Manager to help your servers.
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ServerDashboardScreen;
